import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const T = { vert: '#1e4a1a', or: '#c4a76c', bg: '#faf9f6', border: '#e8e5e0', textPrimary: '#1a1a1a', textSecondary: '#6b7280', textMuted: '#9ca3af', inputBg: '#f5f3ef' };

const AdminResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Minimum 6 caracteres requis'); return; }
    if (password !== confirm) { setError('Les mots de passe ne correspondent pas'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Lien invalide ou expire');
      }
      setSuccess(true);
    } catch (err) {
      // Handle JSON parse errors or network errors gracefully
      const errMsg = err.message || '';
      if (err.name === 'SyntaxError' || errMsg.toLowerCase().includes('json') || errMsg.includes('body stream')) {
        setError('Lien invalide ou expire');
      } else {
        setError(errMsg || 'Lien invalide ou expire');
      }
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { background: T.inputBg, border: `1px solid ${T.border}`, color: T.textPrimary, fontFamily: "'Montserrat', sans-serif" };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: T.bg }}>
        <div className="text-center">
          <AlertCircle className="w-10 h-10 mx-auto mb-4" style={{ color: '#dc2626' }} />
          <p className="text-sm" style={{ color: T.textSecondary, fontFamily: "'Montserrat', sans-serif" }}>Lien invalide.</p>
          <button onClick={() => navigate('/ogd-access-gate')} className="mt-4 text-sm hover:underline" style={{ color: T.vert }}>Retour</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: T.bg }} data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-12">
          <img
            src="/assets/logos/logo-dore.png"
            alt="One Green Dev"
            className="h-16 w-auto object-contain mx-auto mb-4"
          />
          <p className="text-[10px] tracking-[0.3em] uppercase" style={{ fontFamily: "'Montserrat', sans-serif", color: T.or }}>Administration</p>
        </div>

        <div className="rounded-2xl p-8 bg-white" style={{ border: `1px solid ${T.border}`, boxShadow: '0 4px 24px rgba(0,0,0,0.04)' }}>
          {success ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle className="w-10 h-10 mx-auto mb-4" style={{ color: T.vert }} />
              <h2 className="text-lg mb-2" style={{ fontFamily: "'Playfair Display', serif", color: T.textPrimary }}>Mot de passe modifie</h2>
              <p className="text-[13px] mb-6" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textSecondary }}>
                Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
              </p>
              <button onClick={() => navigate('/ogd-access-gate')} data-testid="go-to-login-btn"
                className="w-full py-3 rounded-xl text-[13px] font-semibold text-white transition-all"
                style={{ background: T.vert, fontFamily: "'Montserrat', sans-serif" }}>
                Se connecter
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-lg mb-1" style={{ fontFamily: "'Playfair Display', serif", color: T.textPrimary }}>Nouveau mot de passe</h2>
              <p className="text-[13px] mb-8" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textSecondary }}>
                Choisissez votre nouveau mot de passe.
              </p>

              {error && (
                <div className="mb-6 p-3 rounded-xl flex items-center gap-2 text-sm" data-testid="reset-error"
                  style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.12)', color: '#dc2626', fontFamily: "'Montserrat', sans-serif" }}>
                  <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-[11px] font-medium uppercase tracking-wider mb-2" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>Nouveau mot de passe</label>
                  <input type="password" value={password} onChange={e => { setPassword(e.target.value); setError(''); }} required
                    data-testid="reset-password-input" placeholder="Minimum 6 caracteres"
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-colors focus:ring-2 focus:ring-emerald-200" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-[11px] font-medium uppercase tracking-wider mb-2" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>Confirmer</label>
                  <input type="password" value={confirm} onChange={e => { setConfirm(e.target.value); setError(''); }} required
                    data-testid="reset-confirm-input" placeholder="Confirmer le mot de passe"
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-colors focus:ring-2 focus:ring-emerald-200" style={inputStyle} />
                </div>
                <button type="submit" disabled={loading} data-testid="reset-submit-btn"
                  className="w-full py-3.5 rounded-xl text-[13px] font-semibold transition-all text-white"
                  style={{ background: loading ? 'rgba(30,74,26,0.5)' : T.vert, fontFamily: "'Montserrat', sans-serif" }}>
                  {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Reinitialiser'}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-[11px] mt-8" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>
          One Green Dev &mdash; Espace prive
        </p>
      </div>
    </div>
  );
};

export default AdminResetPasswordPage;
